import React, { useContext } from 'react'
import { ImageBackground } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { LyraSelectSkyBackground } from '../../assets'
import LyraSelectSkyBackgroundMobile from '../../assets/LyraSelectSkyBackgroundMobile.png'
import LyraSelectSkyBackgroundMobilePlus from '../../assets/LyraSelectSkyBackgroundMobilePlus.png'
import { IS_WEB } from '../../constants'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { ProductTypeContext } from '../../utils/productType/productTypeContext'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CloudBackgroundImage } from '../backgrounds/CloudBackgroundImage'
import { Flex1View } from '../content/CommonViews'

const StyledFlex1View = styled(Flex1View)({
  height: '100%',
})

const LayoutContainer = styled.View<{ theme: ThemeType; isMainContentCentered?: boolean; isBackground?: boolean }>(
  ({ theme: { spacing, breakpoints, isAuroraTheme }, isMainContentCentered, isBackground }) => {
    const gap = 16
    const margin = breakpoints.isMinWidthDesktop
      ? 80
      : breakpoints.isMinWidthLaptop
      ? 40
      : breakpoints.isMinWidthTablet
      ? 24
      : 16
    const gridMargin = margin - gap
    return {
      ...(IS_WEB &&
        !isMainContentCentered &&
        breakpoints.isMinWidthLaptop && {
          display: 'grid',
          gridTemplateColumns: `[start] ${gridMargin}px repeat(12, [col-start] 1fr) [col-end] ${gridMargin}px [end]`,
          columnGap: isAuroraTheme ? 0 : spacing['16px'],
        }),
      ...(isBackground && {
        position: IS_WEB ? 'fixed' : 'absolute',
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        ...(IS_WEB && isAuroraTheme && { pointerEvents: 'none' }),
        ...(IS_WEB && breakpoints.isMinWidthLaptop && { gridTemplateRows: '100%' }),
      }),
    }
  },
)

const FormBackground = styled.View<{ theme: ThemeType }>(({ theme: { breakpoints, colors, isAuroraTheme } }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(
    isAuroraTheme,
    colors.components.twoColumnRegistrationLayout.formBackground.background,
  ),
  ...(IS_WEB && breakpoints.isMinWidthLaptop
    ? {
        gridColumnStart: 'start',
        gridColumnEnd: 'col-start 8',
      }
    : {
        width: '100%',
      }),
}))

const SideContentBackground = styled.View<{
  theme: ThemeType
  backgroundColor?: string
  background?: boolean
  auroraBackgroundImageUrl?: string
  isMobileScreen?: boolean
}>(
  ({
    theme: { breakpoints, isDarkMode, isLyraSelect, isAuroraTheme },
    background = true,
    backgroundColor,
    auroraBackgroundImageUrl,
    isMobileScreen,
  }) => ({
    flex: isMobileScreen ? 1 : undefined,
    height: '100%',
    ...(IS_WEB && breakpoints.isMinWidthLaptop
      ? {
          gridColumnStart: 'col-start 8',
          gridColumnEnd: 'end',
        }
      : {
          width: '100%',
        }),
    ...(isAuroraTheme && { position: 'relative', overflow: 'hidden' }),
    ...(background && {
      backgroundColor: isAuroraTheme ? 'transparent' : backgroundColor || (isDarkMode ? '#2A2A2A' : '#F8F5EF'),
      ...(IS_WEB && {
        ...(!isAuroraTheme && {
          backgroundImage: backgroundColor
            ? `linear-gradient(${backgroundColor}, ${backgroundColor})`
            : isDarkMode
            ? 'linear-gradient(165deg, #2A2A2A 8.31%, #2A2A2A 70.02%, #4A3828 105.69%)'
            : 'linear-gradient(165deg, #F8F5EF 8.31%, #F8F5EF 70.02%, #FFE0C8 105.69%)',
        }),
        ...(!isAuroraTheme &&
          isLyraSelect && {
            backgroundImage: `url(${
              breakpoints.isMinWidthMobileS
                ? LyraSelectSkyBackground.uri
                : breakpoints.isMinWidthMobileXs
                ? LyraSelectSkyBackgroundMobilePlus
                : LyraSelectSkyBackgroundMobile
            })`,
            backgroundSize: breakpoints.isMinWidthMobileS ? 'cover' : 'contain',
          }),
        ...(isAuroraTheme &&
          auroraBackgroundImageUrl &&
          IS_WEB && {
            backgroundImage: `url(${auroraBackgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }),
      }),
    }),
  }),
)

// Using a raw <img> because RNW's style processor drops objectFit/objectPosition.
const auroraBackgroundSkyStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'right top',
}
const auroraDarkBackgroundSkyStyle: React.CSSProperties = {
  ...auroraBackgroundSkyStyle,
  objectPosition: 'center center',
}

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme: { breakpoints, spacing } }) => {
  const horizontalMargin = breakpoints.isMinWidthDesktop
    ? '80px'
    : breakpoints.isMinWidthLaptop
    ? spacing['48px']
    : breakpoints.isMinWidthTablet
    ? spacing['24px']
    : breakpoints.isMobileSized
    ? spacing['16px']
    : undefined
  const verticalMargin = breakpoints.isMinWidthTablet
    ? spacing['48px']
    : breakpoints.isMobileSized
    ? spacing['32px']
    : undefined

  return {
    alignItems: 'center',
    margin: `${verticalMargin} ${horizontalMargin}`,
  }
})

const FormContentContainer = styled(ContentContainer)<{ theme: ThemeType }>(({ theme: { breakpoints, spacing } }) => ({
  marginTop: breakpoints.isMinWidthLaptop
    ? spacing['48px']
    : breakpoints.isMinWidthTablet
    ? spacing['24px']
    : breakpoints.isMobileSized
    ? spacing['16px']
    : undefined,
}))

const ValuePropsContent = styled.View<{ theme: ThemeType }>(({ theme: { breakpoints, isAuroraTheme } }) => ({
  ...(!isAuroraTheme && {
    maxWidth: breakpoints.isMinWidthDesktopL
      ? '554px'
      : breakpoints.isMinWidthLaptop
      ? '460px'
      : breakpoints.isMinWidthTablet
      ? '472px'
      : breakpoints.isMobileSized
      ? '467px'
      : undefined,
    minWidth: breakpoints.isMinWidthLaptop ? '347px' : breakpoints.isMobileSized ? '288px' : undefined,
  }),
}))

const CloudBackgroundImageContainer = styled.View({
  flex: 1,
  height: '100%',
})

interface TwoColumnLayoutProps {
  mainContent: React.ReactNode
  isMainContentCentered?: boolean
  showSideContent?: boolean
  sideContent?: React.ReactNode
  sideContentBackgroundColor?: string
}

export const TwoColumnRegistrationLayout: React.FC<TwoColumnLayoutProps> = ({
  mainContent,
  isMainContentCentered = false,
  showSideContent = true,
  sideContent,
  sideContentBackgroundColor,
}) => {
  const { isMinWidthLaptop } = useMediaQuerySize()
  const { isLyraSelect } = useContext(ProductTypeContext)
  const { isAuroraTheme, isDarkMode } = useTheme() as ThemeType
  const backgroundSkyStyle = isDarkMode ? auroraDarkBackgroundSkyStyle : auroraBackgroundSkyStyle

  const {
    assets: { signUpPanelBlueSky },
  } = useTheme() as ThemeType

  const auroraBackgroundImageUrl = useGetResponsiveAssets(signUpPanelBlueSky?.assetNames ?? { defaultAsset: '' })

  const isMobileScreen = !IS_WEB && isAuroraTheme && auroraBackgroundImageUrl

  const sideContentElement = (
    <ContentContainer>
      <ValuePropsContent>{sideContent}</ValuePropsContent>
    </ContentContainer>
  )

  return (
    <StyledFlex1View testID={tID('TwoColumnRegistrationLayout')}>
      {isMinWidthLaptop && (
        <LayoutContainer isMainContentCentered={isMainContentCentered} isBackground>
          <FormBackground style={{ height: '100%' }} />
          <SideContentBackground
            backgroundColor={sideContentBackgroundColor}
            auroraBackgroundImageUrl={auroraBackgroundImageUrl}
          >
            {IS_WEB && isAuroraTheme && <img src={auroraBackgroundImageUrl} style={backgroundSkyStyle} alt='' />}
          </SideContentBackground>
        </LayoutContainer>
      )}
      <LayoutContainer isMainContentCentered={isMainContentCentered}>
        <FormBackground>
          <FormContentContainer>{mainContent}</FormContentContainer>
        </FormBackground>
        {showSideContent && (
          <SideContentBackground
            backgroundColor={sideContentBackgroundColor}
            background={!isMinWidthLaptop}
            auroraBackgroundImageUrl={auroraBackgroundImageUrl}
          >
            {isLyraSelect && !IS_WEB && !isAuroraTheme ? (
              <CloudBackgroundImageContainer>
                <CloudBackgroundImage>{sideContentElement}</CloudBackgroundImage>
              </CloudBackgroundImageContainer>
            ) : isMobileScreen ? (
              <ImageBackground
                source={{ uri: auroraBackgroundImageUrl }}
                resizeMode='cover'
                testID={tID('TwoColumnRegistrationLayout-auroraMobile-backgroundImage')}
              >
                {sideContentElement}
              </ImageBackground>
            ) : (
              sideContentElement
            )}
          </SideContentBackground>
        )}
      </LayoutContainer>
    </StyledFlex1View>
  )
}
